/**
 * One account as a tile, for Settings › Accounts' card view.
 *
 * Not a link — see `ListCard.to`. An account has no page of its own, and the obvious card-wide
 * action, Edit, exists for key-based accounts but not for OAuth ones. Everything lives in the kebab,
 * which is the same menu the table row carries, built by the same `buildAccountRowActions`.
 *
 * ## What is deliberately NOT in here
 *
 * `ReauthorizeAction` — the re-authorize flow's status surface (waiting callout, Cancel, *Reopen the
 * window*) and its confirm dialog. The table mounts one per row; a card must not, because the card
 * grid is VIRTUALIZED: scroll a re-authorizing account out of view and the row unmounts, taking the
 * live popup handle, the nonce, the BroadcastChannel listener and the abandon timer with it, with no
 * error anywhere. The page mounts a single one below the grid instead — the same shape
 * `AccountField` uses for the same reason. The kebab's "Re-authorize" item only asks the page to open
 * the dialog.
 */
import { KebabMenu, type KebabMenuAction } from '@/_shared/components/list/KebabMenu';
import { ListCard } from '@/_shared/components/list/ListCard';
import { Badge } from '@/_shared/components/ui/badge';
import { timeAgo } from '@/_shared/lib/format';
import { destinationTypeIcon } from '@/audiences/icons/destinationTypes';

import { accountConnectedAs, type AccountListRow } from '../../lib/accountList';

interface AccountCardProps {
  account: AccountListRow;
  actions: readonly KebabMenuAction[];
}

export function AccountCard({ account, actions }: AccountCardProps): React.JSX.Element {
  const Icon = destinationTypeIcon(account.destinationType).Icon;
  const connectedAs = accountConnectedAs(account);
  return (
    <ListCard
      title={account.name}
      // Decorative: the card's own text already names the account and its type. The mark is here
      // because it is what someone SCANS for — finding one Facebook account among thirty is the
      // reason this view exists at all.
      titleIcon={
        <span aria-hidden className="flex shrink-0 items-center">
          <Icon className="size-4" />
        </span>
      }
      badges={
        <Badge variant="outline" className="shrink-0 text-xs">
          {account.authLabel}
        </Badge>
      }
      actions={
        actions.length > 0 ? <KebabMenu label={`Actions for ${account.name}`} actions={[...actions]} /> : undefined
      }
      // The OAuth grant's owner, on OAuth accounts only — `accountConnectedAs` returns null for the
      // rest rather than a placeholder.
      description={connectedAs}
      meta={
        <>
          {/* No `MetaChip`: its icon slot would repeat the brand mark already sitting on the title,
              and the type needs no second glyph to be read. */}
          <span className="truncate" title={account.typeLabel}>
            {account.typeLabel}
          </span>
          {/* Without the platform's own id, two accounts a customer named the same are identical
              tiles — and this is the surface that edits and deletes them. Null for most accounts,
              and a null renders nothing rather than an empty chip. */}
          {account.externalId ? <span className="truncate">{account.externalId}</span> : null}
        </>
      }
      footerRight={`Updated ${timeAgo(account.updatedAt)}`}
    />
  );
}
